/**
 * <ia-sweepstakes>, the Sol Passport sweepstakes entry (sections/ia-sweepstakes).
 *
 * Signed out the band is static (a log in link). Signed in it talks to the
 * sweepstakes worker behind the store's app proxy (data-proxy):
 *   GET  /check                  { points_balance }  ENTER wakes once the balance covers data-points
 *   POST /redeem { redemption_option_id, delay_points_deduction: false }  spends the points in the loyalty app
 *   POST /submit {}              logs the entry (the worker keeps the sheet)
 * The worker reads the shopper from the proxy's signed request, so nothing
 * about them travels from here. A failed check or redeem hands the button
 * back with the points untouched; a failed submit after a successful redeem
 * still shows the success copy (the points are spent) and is reported, so
 * the entry can be logged by hand. Each step carries its own where tag.
 */
import { captureError } from 'ia-error-report';

class IaSweepstakes extends HTMLElement {
  connectedCallback() {
    if (this.dataset.state !== 'in') return;
    this.proxy = (this.dataset.proxy || '/apps/sweeps').replace(/\/$/, '');
    this.needed = Number(this.dataset.points) || 0;
    this.option = Number(this.dataset.option) || 0;
    this.offer = this.querySelector('[data-sweeps-offer]');
    this.success = this.querySelector('[data-sweeps-success]');
    this.enter = this.querySelector('[data-sweeps-enter]');
    this.confirm = this.querySelector('[data-sweeps-confirm]');
    this.busyMark = this.querySelector('[data-sweeps-busy]');
    if (!this.enter) return;

    this.onClick = (event) => this.handleClick(event);
    this.addEventListener('click', this.onClick);
    this.controller = new AbortController();
    this.check();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.controller?.abort();
  }

  async request(path, init = {}) {
    const response = await fetch(`${this.proxy}${path}`, {
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      signal: this.controller.signal,
      ...init,
    });
    if (!response.ok) {
      const failed = new Error(`${path} answered ${response.status}`);
      // A refusal (4xx: not enough points, already entered) is the app's
      // answer to the shopper; a 5xx is the app failing and stays an error.
      if (response.status < 500) failed.level = 'warning';
      throw failed;
    }
    return response.json();
  }

  async check() {
    try {
      const data = await this.request('/check');
      this.setEnter(Number(data.points_balance) >= this.needed);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      captureError(error, { where: 'sweeps:check', ...(error.level ? { level: error.level } : {}) });
      this.setEnter(false);
    }
  }

  setEnter(ready) {
    this.enter.disabled = !ready;
    this.enter.textContent = ready ? this.enter.dataset.labelEnter : this.enter.dataset.labelMore;
  }

  handleClick(event) {
    const button = event.target.closest('[data-sweeps-enter], [data-sweeps-yes], [data-sweeps-no], [data-sweeps-again]');
    if (!button || !this.contains(button)) return;
    if (button.hasAttribute('data-sweeps-enter')) {
      this.enter.hidden = true;
      this.confirm.hidden = false;
    } else if (button.hasAttribute('data-sweeps-no')) {
      this.confirm.hidden = true;
      this.enter.hidden = false;
    } else if (button.hasAttribute('data-sweeps-yes')) {
      this.submit();
    } else {
      this.success.hidden = true;
      this.offer.hidden = false;
      this.confirm.hidden = true;
      this.enter.hidden = false;
      this.setEnter(false);
      this.check();
    }
  }

  busy(on) {
    this.busyMark.hidden = !on;
    this.confirm.querySelectorAll('button').forEach((button) => {
      button.disabled = on;
    });
  }

  async submit() {
    this.busy(true);
    try {
      await this.request('/redeem', {
        method: 'POST',
        body: JSON.stringify({ redemption_option_id: this.option, delay_points_deduction: false }),
      });
    } catch (error) {
      this.busy(false);
      if (error?.name === 'AbortError') return;
      captureError(error, { where: 'sweeps:redeem', ...(error.level ? { level: error.level } : {}) });
      this.confirm.hidden = true;
      this.enter.hidden = false;
      return;
    }
    try {
      await this.request('/submit', { method: 'POST', body: '{}' });
    } catch (error) {
      if (error?.name !== 'AbortError') captureError(error, { where: 'sweeps:submit', ...(error.level ? { level: error.level } : {}) });
    }
    this.busy(false);
    this.offer.hidden = true;
    this.success.hidden = false;
  }
}

if (!customElements.get('ia-sweepstakes')) {
  customElements.define('ia-sweepstakes', IaSweepstakes);
}
